import React, { useEffect, useState } from "react";
import { getDocs, collection } from "firebase/firestore";
import PageTemplate from "../templates/PageTemplate";
import { Card, CardContent } from "../components/Card";
import { db } from "../config/firebase";

const collectionsToCount = [
  { key: "users", label: "Utilisateurs", icon: "users" },
  { key: "projects", label: "Projets", icon: "briefcase" },
  { key: "offers", label: "Offres", icon: "tag" },
  { key: "appointments", label: "Rendez-vous", icon: "calendar" },
  { key: "categories", label: "Catégories", icon: "folder" },
  // { key: "availabilities", label: "Disponibilités", icon: "clock" },
];

const APPOINTMENT_STATUS = [
  { key: "pending", label: "En attente", color: "text-yellow-600" },
  { key: "confirmed", label: "Confirmé", color: "text-blue-600" },
  { key: "waitPayment", label: "Att. paiement", color: "text-orange-600" },
  { key: "paymentConfirmed", label: "Payé", color: "text-green-600" },
];

const OFFER_STATUS = [
  { key: "pending", label: "En attente", color: "text-yellow-600" },
  { key: "confirmed", label: "Confirmée", color: "text-green-600" },
  { key: "refused", label: "Refusée", color: "text-red-600" },
];

const PROJECT_STATUS = [
  { key: "pending", label: "En attente", color: "text-yellow-600" },
  { key: "confirmed", label: "Confirmé", color: "text-blue-600" },
  { key: "waitPayment", label: "Att. paiement", color: "text-orange-600" },
  { key: "paymentConfirmed", label: "Payé", color: "text-green-600" },
];

export default function AdminHome() {
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  const [verifiedUsers, setVerifiedUsers] = useState(0);
  const [clientsCount, setClientsCount] = useState(0);
  const [couturiersCount, setCouturiersCount] = useState(0);

  const [appointmentsByStatus, setAppointmentsByStatus] = useState({
    pending: 0,
    confirmed: 0,
    waitPayment: 0,
    paymentConfirmed: 0,
  });

  const [offersByStatus, setOffersByStatus] = useState({
    pending: 0,
    confirmed: 0,
    refused: 0,
  });

  const [projectsByStatus, setProjectsByStatus] = useState({
    pending: 0,
    confirmed: 0,
    waitPayment: 0,
    paymentConfirmed: 0,
  });

  useEffect(() => {
    async function fetchCounts() {
      setLoading(true);
      const result = {};

      for (const c of collectionsToCount) {
        const snap = await getDocs(collection(db, c.key));
        result[c.key] = snap.size;

        if (c.key === "users") {
          const allUsers = snap.docs.map((d) => d.data());
          setVerifiedUsers(allUsers.filter((u) => !!u.emailIsVerified).length);
          setClientsCount(allUsers.filter((u) => !!u.isClient).length);
          setCouturiersCount(allUsers.filter((u) => !u.isClient).length);
        }

        if (c.key === "appointments") {
          const allAppointments = snap.docs.map((d) => d.data());
          const statusCount = {
            pending: 0,
            confirmed: 0,
            waitPayment: 0,
            paymentConfirmed: 0,
          };
          allAppointments.forEach((appt) => {
            const st = appt.status;
            if (statusCount[st] !== undefined) {
              statusCount[st]++;
            }
          });
          setAppointmentsByStatus(statusCount);
        }

        if (c.key === "offers") {
          const allOffers = snap.docs.map((d) => d.data());
          const statusCount = {
            pending: 0,
            confirmed: 0,
            refused: 0,
          };
          allOffers.forEach((offer) => {
            const st = offer.status;
            if (statusCount[st] !== undefined) {
              statusCount[st]++;
            }
          });
          setOffersByStatus(statusCount);
        }

        if (c.key === "projects") {
          const allProjects = snap.docs.map((d) => d.data());
          const statusCount = {
            pending: 0,
            confirmed: 0,
            waitPayment: 0,
            paymentConfirmed: 0,
          };
          allProjects.forEach((project) => {
            const st = project.status;
            if (statusCount[st] !== undefined) {
              statusCount[st]++;
            }
          });
          setProjectsByStatus(statusCount);
        }
      }

      setCounts(result);
      setLoading(false);
    }

    fetchCounts();
  }, []);

  const getIcon = (iconName) => {
    const icons = {
      users: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-8 h-8"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
          />
        </svg>
      ),
      briefcase: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-8 h-8"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 0 0 .75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 0 0-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0 1 12 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 0 1-.673-.38m0 0A2.18 2.18 0 0 1 3 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 0 1 3.413-.387m7.5 0V5.25A2.25 2.25 0 0 0 13.5 3h-3a2.25 2.25 0 0 0-2.25 2.25v.894m7.5 0a48.667 48.667 0 0 0-7.5 0M12 12.75h.008v.008H12v-.008Z"
          />
        </svg>
      ),
      tag: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-8 h-8"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z"
          />
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6Z" />
        </svg>
      ),
      calendar: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-8 h-8"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
          />
        </svg>
      ),
      folder: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-8 h-8"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z"
          />
        </svg>
      ),
      clock: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-8 h-8"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
          />
        </svg>
      ),
    };
    return icons[iconName] || null;
  };

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Welcome Header */}
          <Card className="bg-gradient-to-r from-red-50 to-rose-50 border-red-200">
            <CardContent className="flex items-center gap-4 py-8">
              <div className="p-4 bg-[#801111] rounded-full">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-10 h-10 text-white"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0 1 18 16.5h-2.25m-7.5 0h7.5m-7.5 0-1 3m8.5-3 1 3m0 0 .5 1.5m-.5-1.5h-9.5m0 0-.5 1.5m.75-9 3-3 2.148 2.148A12.061 12.061 0 0 1 16.5 7.605"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Bienvenue, Admin
                </h1>
                <p className="text-gray-600">
                  Vue d'ensemble sur ZZIGS
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {collectionsToCount.map((c) => (
              <Card
                key={c.key}
                className="hover:shadow-lg transition-shadow duration-300"
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-red-100 rounded-lg text-[#801111]">
                      {getIcon(c.icon)}
                    </div>
                    <div className="text-right">
                      <div className="text-4xl font-bold text-[#801111] mb-1">
                        {counts[c.key] ?? "..."}
                      </div>
                      <div className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                        {c.label}
                      </div>
                    </div>
                  </div>

                  {/* Users Details */}
                  {c.key === "users" && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Vérifiés</span>
                        <span className="font-semibold text-green-600">
                          {verifiedUsers}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Clients</span>
                        <span className="font-semibold text-blue-600">
                          {clientsCount}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Couturiers</span>
                        <span className="font-semibold text-purple-600">
                          {couturiersCount}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Appointments Details */}
                  {c.key === "appointments" && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                      {APPOINTMENT_STATUS.map(({ key, label, color }) => (
                        <div key={key} className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">{label}</span>
                          <span className={`font-semibold ${color}`}>
                            {appointmentsByStatus[key] ?? 0}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Offers Details */}
                  {c.key === "offers" && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                      {OFFER_STATUS.map(({ key, label, color }) => (
                        <div key={key} className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">{label}</span>
                          <span className={`font-semibold ${color}`}>
                            {offersByStatus[key] ?? 0}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Projects Details */}
                  {c.key === "projects" && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                      {PROJECT_STATUS.map(({ key, label, color }) => (
                        <div key={key} className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">{label}</span>
                          <span className={`font-semibold ${color}`}>
                            {projectsByStatus[key] ?? 0}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      }
    />
  );
}